import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { format, differenceInMinutes } from "date-fns";
import { MessageStatus } from "@common/models/message-log";
import { UserMessage } from "@bt-common/schemas/bt/user-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Copy, MoreHorizontal, ExternalLink, RefreshCw, Pencil, Trash, FileText, SendHorizontal, AlertCircle, Eye, CheckCircle, Clock } from 'lucide-react';
import { ContactCell } from '@common-components/contact-cell/ContactCell';
import { toast } from 'sonner';
import { Temporal } from '@js-temporal/polyfill';
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
 // Assuming sonner or similar is available, otherwise alert or console

interface MessagesTableProps {
    messages: UserMessage[];
    onMessageClick: (message: UserMessage) => void;
    onAcknowledge: (messageId: string) => void;
}

const getStatusColor = (status: string | undefined) => {
    switch (status) {
        case 'pending': return 'bg-yellow-500';
        case 'sent': return 'bg-blue-500';
        case 'delivered': return 'bg-green-500';
        case 'read': return 'bg-green-700';
        case 'confirmed': return 'bg-green-600';
        case 'error': return 'bg-red-500';
        case 'failed': return 'bg-red-500';
        case 'cancelled': return 'bg-gray-400';
        default: return 'bg-gray-500';
    }
};

const getBadgeClassName = (status: string | undefined) => {
    return `${getStatusColor(status ?? '')} text-white hover:${getStatusColor(status ?? '')}`;
};

const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
};

/**
 * Calculate human-readable duration between two dates using Temporal API
 */
const getHumanReadableDuration = (createdAt: string | Date | undefined, dueDate: string | Date | undefined): string => {
    if (!createdAt || !dueDate) return '';
    try {
        const createdAtStr = createdAt instanceof Date ? createdAt.toISOString() : new Date(createdAt).toISOString();
        const dueDateStr = dueDate instanceof Date ? dueDate.toISOString() : new Date(dueDate).toISOString();
        
        const created = Temporal.Instant.from(createdAtStr);
        const due = Temporal.Instant.from(dueDateStr);
        
        // Calculate duration (createdAt - dueDate, should be positive since createdAt is after dueDate)
        const duration = created.since(due);
        
        // Round to largest unit of day and smallest unit of minute
        const rounded = duration.round({ largestUnit: 'day', smallestUnit: 'minute' });
        
        // Format as human-readable string (e.g., "4 hours, 3 minutes")
        return rounded.toLocaleString('en', { style: 'long' });
    } catch (e) {
        console.error('Error calculating duration:', e);
        return 'unknown duration';
    }
};

/**
 * Check if a message was created more than 30 minutes after its due date.
 * Such messages will never be attempted to send.
 */
const isMessageTooLate = (message: UserMessage): boolean => {
    if (!message.createdAt || !message.dueDate) return false;
    
    const createdAt = new Date(message.createdAt);
    const dueDate = new Date(message.dueDate);
    
    // Calculate difference in minutes (positive if createdAt is after dueDate)
    const diffMinutes = differenceInMinutes(createdAt, dueDate);
    
    return diffMinutes > 30;
};

export function MessagesTable({ messages, onMessageClick, onAcknowledge }: MessagesTableProps) {
    const { t } = useTranslation(namespace);

    if (messages.length === 0) {
        return <div className="text-center py-10 text-gray-500">{t('messagesTable.noMessagesFound')}</div>;
    }

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success(t('messagesTable.campaignIdCopied'));
    };

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[50px]">{t('messagesTable.actions')}</TableHead>
                        <TableHead>{t('messagesTable.status')}</TableHead>
                        <TableHead>{t('messagesTable.campaignId')}</TableHead>
                        <TableHead>{t('messagesTable.dueDate')}</TableHead>
                        <TableHead>{t('messagesTable.contact')}</TableHead>
                        <TableHead>{t('messagesTable.owner')}</TableHead>
                        <TableHead>{t('messagesTable.details')}</TableHead>
                        <TableHead>{t('messagesTable.error')}</TableHead>
                        <TableHead>{t('messagesTable.failureReason')}</TableHead>
                        <TableHead>{t('messagesTable.recurring')}</TableHead>
                        <TableHead>{t('messagesTable.deleted')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {messages.map((message) => {
                        const contactIdentifier = message.contactList?.[0]?.mobile || message.contactList?.[0]?.id || message.contactName || 'unknown';
                        const uniqueKey = `${contactIdentifier}-${message.message}-${message.dueDate}`;
                        const hashColor = stringToColor(uniqueKey);
                        const campaignId = (message as any).campaignId || '';
                        const campaignColor = stringToColor(campaignId);
                        const isTooLate = isMessageTooLate(message);
                        
                        return (
                            <TableRow 
                                key={message._id} 
                                className={`hover:bg-muted/50 ${isTooLate ? 'bg-orange-50 dark:bg-orange-950/20' : ''}`}
                            >
                                <TableCell>
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8"
                                        onClick={() => onMessageClick(message)}
                                        title={t('messagesTable.viewDetails')}
                                    >
                                        <Eye className="h-4 w-4" />
                                        <span className="sr-only">{t('messagesTable.viewDetails')}</span>
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAcknowledge(message._id);
                                        }}
                                        title={t('messagesTable.acknowledge')}
                                    >
                                        <CheckCircle className="h-4 w-4" />
                                        <span className="sr-only">{t('messagesTable.acknowledge')}</span>
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col gap-1">
                                        <Badge className={getBadgeClassName(message.status)}>
                                            {message.status}
                                        </Badge>
                                        {isTooLate && (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Badge variant="outline" className="border-orange-500 text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-950/50 flex items-center gap-1 w-fit cursor-help">
                                                        <Clock className="h-3 w-3" />
                                                        <span className="text-[10px] font-semibold">{t('messagesTable.tooLate')}</span>
                                                    </Badge>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <div className="max-w-xs">
                                                        <p className="font-semibold mb-1">{t('messagesTable.messageWillNeverBeSent')}</p>
                                                        <p className="text-xs">
                                                            {t('messagesTable.messageCreatedLate', { duration: getHumanReadableDuration(message.createdAt, message.dueDate) })}
                                                        </p>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {campaignId ? (
                                        <div className="flex items-center gap-2">
                                            <span style={{ color: campaignColor }} className="font-mono text-xs font-bold">
                                                {campaignId.slice(0, 8)}...
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => handleCopy(campaignId)}
                                                title={t('messagesTable.copyCampaignId')}
                                            >
                                                <Copy className="h-3 w-3" />
                                                <span className="sr-only">{t('messagesTable.copyCampaignId')}</span>
                                            </Button>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <span className="font-medium">{format(new Date(message.dueDate ?? 0), 'dd-MM-yyyy')}</span>
                                        <span className="text-xs text-muted-foreground">{format(new Date(message.dueDate ?? 0), 'HH:mm')}</span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <ContactCell 
                                        contactList={message.contactList} 
                                        contactName={message.contactName} 
                                    />
                                </TableCell>
                                <TableCell>
                                    <span className="text-sm font-mono" title={String(message.owner)}>
                                        {/* Ideally we would populate this, but for now showing ID */}
                                        {(message as any).owner_populated?.email || (typeof message.owner === 'object' ? (message.owner as any).email : message.owner)}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <p className="max-w-[200px] truncate text-xs" title={message.message ?? undefined}>
                                        {message.message}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    {message.sendFailureError ? (
                                        <Badge variant="outline" className="text-[10px] whitespace-normal break-all max-w-[200px] border-red-500 text-red-600 leading-tight py-1">
                                            {typeof message.sendFailureError === 'object' ? JSON.stringify(message.sendFailureError).slice(0, 200) : String(message.sendFailureError)}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {message.sendFailureReason ? (
                                        <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600">
                                            {message.sendFailureReason}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {message.isRecurring ? (
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="border-purple-500 text-purple-500">{t('messagesTable.recurringLabel')}</Badge>
                                            <span 
                                                className="w-3 h-3 rounded-full" 
                                                style={{ backgroundColor: hashColor }}
                                                title={t('messagesTable.seriesColor')}
                                            />
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {(message as any).deleted ? (
                                        <Badge className="bg-red-900 text-white hover:bg-red-800">{t('messagesTable.deletedLabel')}</Badge>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}
